import React, { useState, useEffect, useMemo } from "react";
import { MdPersonAdd, MdSearch } from "react-icons/md";
import { API_BASE_URL } from "../../utils/api";
import { confirmDialog } from "../../utils/dialogs";
import { CloseIcon } from "../shared/icons/Icons";

function AdminManagement({ onLogout, showToast }) {
  const [admins, setAdmins] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    password: "",
    role: "principal",
    campus_id: "",
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editPasswordConfirm, setEditPasswordConfirm] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = async () => {
    const token = localStorage.getItem("als_token");
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      const [admRes, camRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admins`, { headers: authHeader }),
        fetch(`${API_BASE_URL}/api/campuses`, { headers: authHeader }),
      ]);

      if (admRes.status === 401 || camRes.status === 401) {
        onLogout();
        return;
      }
      if (!admRes.ok || !camRes.ok)
        throw new Error("Failed to fetch admin data");

      const [admData, camData] = await Promise.all([
        admRes.json(),
        camRes.json(),
      ]);
      setAdmins(Array.isArray(admData) ? admData : []);
      setCampuses(Array.isArray(camData) ? camData : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("als_token");
    const isViewer = newAdmin.role === "viewer";
    const requiresCampus =
      newAdmin.role === "administrator" || newAdmin.role === "accountant" || newAdmin.role === "principal" || newAdmin.role === "admin";
    if (requiresCampus && !newAdmin.campus_id) {
      showToast(
        "error",
        "Campus Required",
        "Please assign a campus for administrator and accountant accounts.",
      );
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: newAdmin.username,
          password: newAdmin.password,
          role: newAdmin.role,
          is_active: true,
          campus_ids: !isViewer && newAdmin.campus_id
            ? [parseInt(newAdmin.campus_id, 10)]
            : [],
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to create admin");
      }
      showToast(
        "success",
        "User Created",
        `${newAdmin.username} has been added.`,
      );
      setShowAddModal(false);
      setNewAdmin({ username: "", password: "", role: "principal", campus_id: "" });
      fetchData();
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const handleManageClick = (admin) => {
    setSelectedAdmin(admin);
    setEditUsername(admin.username);
    setEditPassword("");
    setEditPasswordConfirm("");
    setEditMode(false);
    setShowManageModal(true);
    setShowCredentials(false);
    setTempPassword(null);
  };

  const handleAssignCampus = async (campusId) => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/assign-campus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedAdmin.id,
          campus_id: campusId,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to assign campus");
      }
      showToast(
        "success",
        "Campus Assigned",
        "Campus has been assigned to this admin.",
      );
      fetchData();
      setShowManageModal(false);
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const handleUnassignCampus = async (campusId) => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admins/${selectedAdmin.id}/unassign-campus/${campusId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to unassign campus");
      }
      showToast(
        "success",
        "Campus Unassigned",
        "Campus has been removed from this admin.",
      );
      fetchData();
      setShowManageModal(false);
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const handleResetPassword = async () => {
    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admins/${selectedAdmin.id}/reset-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to reset password");
      }
      const data = await res.json();
      setTempPassword(data.temporary_password);
      setShowCredentials(true);
      showToast(
        "success",
        "Password Reset",
        "Temporary password has been generated.",
      );
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const handleDeleteAdmin = async () => {
    if (
      !(await confirmDialog(
        `Are you sure you want to delete admin "${selectedAdmin.username}"? This action cannot be undone.`,
        { title: "Delete Admin", confirmLabel: "Delete Admin", variant: "danger" },
      ))
    ) {
      return;
    }

    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admins/${selectedAdmin.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to delete admin");
      }
      showToast(
        "success",
        "Admin Deleted",
        `Admin "${selectedAdmin.username}" has been deleted.`,
      );
      fetchData();
      setShowManageModal(false);
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const handleChangeUsername = async () => {
    if (!editUsername.trim()) {
      showToast("error", "Validation Error", "Username cannot be empty");
      return;
    }
    if (editUsername === selectedAdmin.username) {
      showToast("info", "No Change", "Username is the same");
      return;
    }

    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admins/${selectedAdmin.id}/change-username`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ new_username: editUsername }),
        },
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to change username");
      }
      showToast(
        "success",
        "Username Changed",
        `Username updated to "${editUsername}".`,
      );
      setEditMode(false);
      fetchData();
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const handleChangePassword = async () => {
    if (!editPassword || editPassword.length < 6) {
      showToast(
        "error",
        "Validation Error",
        "Password must be at least 6 characters",
      );
      return;
    }
    if (editPassword !== editPasswordConfirm) {
      showToast("error", "Validation Error", "Passwords do not match");
      return;
    }

    const token = localStorage.getItem("als_token");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admins/${selectedAdmin.id}/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ new_password: editPassword }),
        },
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to change password");
      }
      showToast(
        "success",
        "Password Changed",
        "Admin password has been updated.",
      );
      setEditPassword("");
      setEditPasswordConfirm("");
      setEditMode(false);
      fetchData();
    } catch (err) {
      showToast("error", "Error", err.message);
    }
  };

  const getAssignedCampusNames = (admin) => {
    if (admin.role === "viewer") {
      return "Global analytics access";
    }
    if (!admin.assigned_campuses || admin.assigned_campuses.length === 0) {
      return "No campuses assigned";
    }
    return admin.assigned_campuses.map((c) => c.name).join(", ");
  };

  const userMetrics = useMemo(
    () => ({
      total: admins.length,
      active: admins.filter((admin) => admin.is_active).length,
      viewers: admins.filter((admin) => admin.role === "viewer").length,
      assigned: admins.filter(
        (admin) =>
          admin.role === "viewer" ||
          (admin.assigned_campuses && admin.assigned_campuses.length > 0),
      ).length,
    }),
    [admins],
  );

  const filteredAdmins = useMemo(() => {
    const search = userSearch.trim().toLowerCase();

    return admins.filter((admin) => {
      const campusAccess = getAssignedCampusNames(admin).toLowerCase();
      const searchableText = `${admin.username} ${admin.role} ${campusAccess}`.toLowerCase();
      const matchesSearch = search ? searchableText.includes(search) : true;
      const matchesRole = roleFilter === "all" || admin.role === roleFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? admin.is_active : !admin.is_active);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [admins, userSearch, roleFilter, statusFilter]);

  const hasActiveFilters =
    userSearch.trim() || roleFilter !== "all" || statusFilter !== "all";

  const resetUserFilters = () => {
    setUserSearch("");
    setRoleFilter("all");
    setStatusFilter("all");
  };

  const activePercent =
    userMetrics.total > 0
      ? Math.round((userMetrics.active / userMetrics.total) * 100)
      : 0;

  return (
    <div className="dashboard-content user-management-page">
      <div className="admin-mgmt-actions-row">
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <MdPersonAdd aria-hidden="true" />
          Add User
        </button>
      </div>

      <section className="admin-mgmt-stats-row">
        <div className="admin-mgmt-stat-card accent-navy">
          <span className="admin-mgmt-stat-label">Total Users</span>
          <strong className="admin-mgmt-stat-value">{userMetrics.total}</strong>
        </div>
        <div className="admin-mgmt-stat-card accent-green">
          <span className="admin-mgmt-stat-label">Active</span>
          <strong className="admin-mgmt-stat-value">{userMetrics.active}</strong>
          <small className="admin-mgmt-stat-sub">{activePercent}% of users</small>
        </div>
        <div className="admin-mgmt-stat-card accent-gold">
          <span className="admin-mgmt-stat-label">Viewer Roles</span>
          <strong className="admin-mgmt-stat-value">{userMetrics.viewers}</strong>
        </div>
        <div className="admin-mgmt-stat-card accent-red">
          <span className="admin-mgmt-stat-label">With Access</span>
          <strong className="admin-mgmt-stat-value">{userMetrics.assigned}</strong>
        </div>
      </section>

      <section className="user-management-toolbar">
        <div className="user-filter-field user-search-field">
          <div className="user-search-input-wrap">
            <MdSearch aria-hidden="true" />
            <input
              id="user-search"
              type="search"
              aria-label="Search user"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search by name, role, or campus"
            />
          </div>
        </div>
        <div className="user-filter-field">
          <select
            id="role-filter"
            aria-label="Role filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All roles</option>
            <option value="principal">Principal</option>
            <option value="administrator">Administrator</option>
            <option value="accountant">Accountant</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
            <option value="superadmin">Superadmin</option>
          </select>
        </div>
        <div className="user-filter-field">
          <select
            id="status-filter"
            aria-label="Status filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="user-management-toolbar-actions">
          {hasActiveFilters && (
            <button
              type="button"
              className="btn-secondary"
              onClick={resetUserFilters}
            >
              Clear
            </button>
          )}
        </div>
      </section>

      <div className="student-table-card user-management-table-card">
        <div className="admin-mgmt-table-header">
          <h3>All Users</h3>
          <span className="admin-mgmt-table-pill">
            {filteredAdmins.length} {filteredAdmins.length === 1 ? "user" : "users"}
          </span>
        </div>
        {filteredAdmins.length > 0 ? (
          <table className="student-table user-management-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Campus Access</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map((admin) => (
                <tr key={admin.id}>
                  <td>
                    <div className="user-cell">
                      <span className="user-avatar-token">
                        {admin.username?.slice(0, 2).toUpperCase() || "U"}
                      </span>
                      <div className="user-name-cell">
                        <strong>{admin.username}</strong>
                        <small>User ID #{admin.id}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge-small ${admin.role}`}>
                      {admin.role.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className="user-access-cell">
                      {getAssignedCampusNames(admin)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`user-status-pill ${
                        admin.is_active ? "active" : "inactive"
                      }`}
                    >
                      {admin.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-text user-manage-link"
                      onClick={() => handleManageClick(admin)}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="user-empty-state">
            <h3>No users found</h3>
            <p>Adjust the filters or add a new user account.</p>
            {hasActiveFilters ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={resetUserFilters}
              >
                Clear Filters
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                Add User
              </button>
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create User Account</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateAdmin}>
                <div className="reg-form-group">
                  <label className="reg-form-label">Username</label>
                  <input
                    type="text"
                    className="reg-input"
                    value={newAdmin.username}
                    onChange={(e) =>
                      setNewAdmin({ ...newAdmin, username: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="reg-form-group">
                  <label className="reg-form-label">Password</label>
                  <input
                    type="password"
                    className="reg-input"
                    value={newAdmin.password}
                    onChange={(e) =>
                      setNewAdmin({ ...newAdmin, password: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="reg-form-group">
                  <label className="reg-form-label">Role</label>
                  <select
                    className="reg-input"
                    value={newAdmin.role}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        role: e.target.value,
                        campus_id:
                          e.target.value === "viewer"
                            ? ""
                            : newAdmin.campus_id,
                      })
                    }
                    required
                  >
                    <option value="principal">Principal (full campus access)</option>
                    <option value="administrator">
                      Administrator (campus ops, no fees)
                    </option>
                    <option value="accountant">
                      Accountant (fee management only)
                    </option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                {newAdmin.role !== "viewer" ? (
                  <div className="reg-form-group">
                    <label className="reg-form-label">
                      Assign Campus
                      {(newAdmin.role === "principal" ||
                        newAdmin.role === "administrator" ||
                        newAdmin.role === "accountant" ||
                        newAdmin.role === "admin") &&
                        " *"}
                    </label>
                    <select
                      className="reg-input"
                      value={newAdmin.campus_id}
                      onChange={(e) =>
                        setNewAdmin({ ...newAdmin, campus_id: e.target.value })
                      }
                      required={
                        newAdmin.role === "principal" ||
                        newAdmin.role === "administrator" ||
                        newAdmin.role === "accountant" ||
                        newAdmin.role === "admin"
                      }
                    >
                      <option value="">
                        {newAdmin.role === "principal" ||
                        newAdmin.role === "administrator" ||
                        newAdmin.role === "accountant" ||
                        newAdmin.role === "admin"
                          ? "Select a campus (required)"
                          : "Select a campus (optional)"}
                      </option>
                      {campuses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="admin-empty-note">
                    Viewer accounts are global and can view analytics for all
                    campuses.
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Create Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showManageModal && selectedAdmin && (
        <div className="modal-overlay admin-manage-overlay">
          <div className="modal-content admin-manage-modal">
            <div className="admin-manage-header">
              <div>
                <span className="admin-manage-kicker">User Account</span>
                <h3>Manage {selectedAdmin.username}</h3>
                <p>Review access and update account credentials.</p>
              </div>
              <button
                type="button"
                className="challan-close-btn"
                onClick={() => setShowManageModal(false)}
                aria-label="Close manage admin modal"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="admin-manage-body">
              {/* Campus Assignments Section */}
              {selectedAdmin.role !== "viewer" ? (
              <section className="admin-manage-section">
                <div className="admin-section-heading">
                  <div>
                    <h4>Campus Access</h4>
                    <p>Campuses this admin can manage.</p>
                  </div>
                  <span className="admin-count-badge">
                    {selectedAdmin.assigned_campuses?.length || 0} assigned
                  </span>
                </div>
                {selectedAdmin.assigned_campuses &&
                selectedAdmin.assigned_campuses.length > 0 ? (
                  <div className="admin-campus-list">
                    {selectedAdmin.assigned_campuses.map((campus) => (
                      <div key={campus.id} className="admin-campus-item">
                        <span>{campus.name}</span>
                        <button
                          type="button"
                          className="admin-link-danger"
                          onClick={() => handleUnassignCampus(campus.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="admin-empty-note">
                    No campuses assigned yet.
                  </div>
                )}

                {/* Assign New Campus */}
                <label className="admin-field-label">Assign New Campus</label>
                <select
                  className="reg-select"
                  id="new-campus-select"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAssignCampus(parseInt(e.target.value, 10));
                      e.target.value = "";
                    }
                  }}
                >
                  <option value="">Select a campus to assign</option>
                  {campuses
                    .filter(
                      (c) =>
                        !selectedAdmin.assigned_campuses ||
                        !selectedAdmin.assigned_campuses.some(
                          (ac) => ac.id === c.id,
                        ),
                    )
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                  ))}
                </select>
              </section>
              ) : (
              <section className="admin-manage-section">
                <div className="admin-section-heading">
                  <div>
                    <h4>Global Analytics Access</h4>
                    <p>Viewer accounts can access analytics across all campuses.</p>
                  </div>
                  <span className="admin-count-badge">Global</span>
                </div>
                <div className="admin-empty-note">
                  No campus assignment is required for this user.
                </div>
              </section>
              )}

              {/* Credentials Section */}
              <section className="admin-manage-section">
                <div className="admin-section-heading">
                  <div>
                    <h4>Credentials</h4>
                    <p>View, reset, or update this administrator account.</p>
                  </div>
                  <div className="admin-inline-actions">
                    <button
                      type="button"
                      className={`admin-action-light ${editMode ? "active" : ""}`}
                      onClick={() => setEditMode(!editMode)}
                      style={{
                        padding: "0.4rem 0.8rem",
                        backgroundColor: editMode ? "#ff9800" : "#1976d2",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                      }}
                    >
                      {editMode ? "✓ Done" : "✏ Edit"}
                    </button>
                    <button
                      type="button"
                      className={`admin-action-light ${showCredentials ? "active" : ""}`}
                      onClick={() => setShowCredentials(!showCredentials)}
                      style={{
                        padding: "0.4rem 0.8rem",
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                      }}
                    >
                      {showCredentials ? "🔒 Hide" : "🔓 Show"}
                    </button>
                  </div>
                </div>

                {showCredentials && (
                  <div className="admin-credential-panel">
                    {!editMode ? (
                      <>
                        <div style={{ marginBottom: "0.75rem" }}>
                          <label
                            style={{
                              fontSize: "0.85rem",
                              color: "#666",
                              display: "block",
                              marginBottom: "0.3rem",
                            }}
                          >
                            Username:
                          </label>
                          <div
                            style={{
                              padding: "0.75rem",
                              backgroundColor: "white",
                              borderRadius: "4px",
                              border: "1px solid #ddd",
                              fontFamily: "monospace",
                              fontSize: "0.9rem",
                              wordBreak: "break-all",
                              fontWeight: "500",
                            }}
                          >
                            {selectedAdmin.username}
                          </div>
                        </div>

                        {tempPassword ? (
                          <div style={{ marginBottom: "0.75rem" }}>
                            <label
                              style={{
                                fontSize: "0.85rem",
                                color: "#666",
                                display: "block",
                                marginBottom: "0.3rem",
                              }}
                            >
                              Temporary Password:
                            </label>
                            <div
                              style={{
                                padding: "0.75rem",
                                backgroundColor: "#fffbe6",
                                borderRadius: "4px",
                                border: "2px solid #fbc02d",
                                fontFamily: "monospace",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                wordBreak: "break-all",
                              }}
                            >
                              {tempPassword}
                            </div>
                            <p
                              style={{
                                fontSize: "0.8rem",
                                color: "#d32f2f",
                                marginTop: "0.5rem",
                              }}
                            >
                              ⚠ Share this password with the admin. They should
                              change it on first login.
                            </p>
                          </div>
                        ) : (
                          <p style={{ fontSize: "0.85rem", color: "#666" }}>
                            Click "Reset Password" or use "Edit" to change
                            password
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ marginBottom: "0.75rem" }}>
                          <label
                            style={{
                              fontSize: "0.85rem",
                              color: "#666",
                              display: "block",
                              marginBottom: "0.3rem",
                            }}
                          >
                            Change Username:
                          </label>
                          <input
                            type="text"
                            value={editUsername}
                            onChange={(e) => setEditUsername(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "0.75rem",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              fontFamily: "monospace",
                              fontSize: "0.9rem",
                              boxSizing: "border-box",
                            }}
                          />
                          <button
                            type="button"
                            className="admin-action-primary"
                            onClick={handleChangeUsername}
                            style={{
                              marginTop: "0.5rem",
                              padding: "0.5rem 1rem",
                              backgroundColor: "#4caf50",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "500",
                            }}
                          >
                            Update Username
                          </button>
                        </div>

                        <div
                          style={{
                            marginBottom: "0.75rem",
                            borderTop: "1px solid #ddd",
                            paddingTop: "1rem",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "0.85rem",
                              color: "#666",
                              display: "block",
                              marginBottom: "0.3rem",
                            }}
                          >
                            Change Password:
                          </label>
                          <input
                            type="password"
                            placeholder="New password (min 6 characters)"
                            value={editPassword}
                            onChange={(e) => setEditPassword(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "0.75rem",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              fontFamily: "monospace",
                              fontSize: "0.9rem",
                              boxSizing: "border-box",
                              marginBottom: "0.5rem",
                            }}
                          />
                          <input
                            type="password"
                            placeholder="Confirm password"
                            value={editPasswordConfirm}
                            onChange={(e) =>
                              setEditPasswordConfirm(e.target.value)
                            }
                            style={{
                              width: "100%",
                              padding: "0.75rem",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              fontFamily: "monospace",
                              fontSize: "0.9rem",
                              boxSizing: "border-box",
                              marginBottom: "0.5rem",
                            }}
                          />
                          <button
                            type="button"
                            className="admin-action-primary"
                            onClick={handleChangePassword}
                            style={{
                              padding: "0.5rem 1rem",
                              backgroundColor: "#1976d2",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "500",
                            }}
                          >
                            Update Password
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </section>
            </div>

            {/* Action Buttons */}
            <div className="admin-manage-actions">
              <button
                type="button"
                className="admin-action-secondary"
                onClick={handleResetPassword}
                style={{
                  padding: "0.75rem",
                  backgroundColor: "#ff9800",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.9rem",
                }}
              >
                🔑 Reset Password
              </button>
              <button
                type="button"
                className="admin-action-danger"
                onClick={handleDeleteAdmin}
                style={{
                  padding: "0.75rem",
                  backgroundColor: "#d32f2f",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.9rem",
                }}
              >
                🗑 Delete Admin
              </button>
            </div>

            <div className="admin-manage-close">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowManageModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManagement;